import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { filterSchemes, initialOption, MedicalAidPick, optionLabel } from 'src/app/core/scheme-reference/scheme-pick';
import type { SchemeEntry, SchemeOptionEntry } from 'src/app/core/scheme-reference/scheme-reference-loader';

/** What the picker starts on: the saved codes, or the saved text when the medical aid was never linked. */
export interface MedicalAidPickerInitial {
  schemeCode?: string | null;
  planCode?: string | null;
  optionCode?: string | null;
  medicalAid?: string | null;
  medicalAidPlan?: string | null;
}

/**
 * Medical aid, plan and option from the scheme reference Connect publishes, with "Can't find my medical aid" for
 * typing it instead (tasks/scheme-linking-central.md). The host passes the schemes, so the signed-out invite wizard
 * and the signed-in screens can each load them their own way.
 */
@Component({
  selector: 'app-medical-aid-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, MatAutocompleteModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule],
  templateUrl: './medical-aid-picker.component.html',
  styleUrls: ['./medical-aid-picker.component.scss'],
})
export class MedicalAidPickerComponent implements OnInit, OnChanges {
  @Input() schemes: SchemeEntry[] = [];
  @Input() initial: MedicalAidPickerInitial | null = null;
  /** "Your medical aid" for patients, "Medical aid" for the practice. */
  @Input() label = 'Medical aid';
  @Input() notListedLabel = "Can't find my medical aid";
  @Output() pickChange = new EventEmitter<MedicalAidPick | null>();

  search = '';
  matches: SchemeEntry[] = [];
  scheme: SchemeEntry | null = null;
  option: SchemeOptionEntry | null = null;
  notListed = false;
  freeAid = '';
  freePlan = '';
  private applied = false;

  readonly optionLabel = optionLabel;

  ngOnInit(): void {
    this.applyInitial();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    if (changes?.['initial'] && !changes['initial'].firstChange) this.startAgain();
    this.matches = filterSchemes(this.schemes, this.scheme ? '' : this.search);
    this.applyInitial();
  }

  onSearch(term: unknown): void {
    if (typeof term !== 'string') return; // a pick writes the scheme itself back through the same change
    this.search = term;
    this.matches = filterSchemes(this.schemes, term);
    if (this.scheme && term !== this.scheme.name) {
      this.scheme = null;
      this.option = null;
      this.pickChange.emit(null);
    }
  }

  selectScheme(scheme: SchemeEntry): void {
    this.scheme = scheme;
    this.search = scheme.name;
    this.notListed = false;
    this.option = scheme.options.length === 1 ? scheme.options[0] : null;
    this.emit();
  }

  selectOption(option: SchemeOptionEntry): void {
    this.option = option;
    this.emit();
  }

  chooseNotListed(): void {
    this.notListed = true;
    this.scheme = null;
    this.option = null;
    if (!this.freeAid && this.search) this.freeAid = this.search;
    this.emit();
  }

  chooseFromList(): void {
    this.notListed = false;
    this.search = '';
    this.matches = filterSchemes(this.schemes, '');
    this.emit();
  }

  freeTextChanged(medicalAid: string, medicalAidPlan: string): void {
    this.freeAid = medicalAid;
    this.freePlan = medicalAidPlan;
    this.emit();
  }

  displayScheme = (value: SchemeEntry | string | null): string => (typeof value === 'string' ? value : value?.name ?? '');

  private emit(): void {
    if (this.notListed) {
      this.pickChange.emit(this.freeAid.trim() ? { freeText: { medicalAid: this.freeAid, medicalAidPlan: this.freePlan } } : null);
    } else {
      this.pickChange.emit(this.scheme && this.option ? { scheme: this.scheme, option: this.option } : null);
    }
  }

  private startAgain(): void {
    this.applied = false;
    this.scheme = null;
    this.option = null;
    this.notListed = false;
    this.search = '';
    this.freeAid = '';
    this.freePlan = '';
  }

  /** Once: the saved codes when the list has them, else the saved text as "not listed". */
  private applyInitial(): void {
    const start = this.initial;
    if (this.applied || !start) return;
    if (start.schemeCode) {
      if (!this.schemes.length) return; // wait for the list
      const scheme = this.schemes.find((s) => s.code === start.schemeCode);
      if (scheme) {
        this.applied = true;
        this.scheme = scheme;
        this.search = scheme.name;
        this.option = initialOption(scheme, start.planCode ?? null, start.optionCode ?? null);
        return;
      }
    }
    this.applied = true;
    if (start.medicalAid) {
      this.notListed = true;
      this.freeAid = start.medicalAid;
      this.freePlan = start.medicalAidPlan ?? '';
    }
  }
}
